import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react'

import { getRouteApi } from '@tanstack/react-router'

import { GisHeaderToolbar } from './GisHeaderToolbar'
import { GisMapRegion } from './GisMapRegion'
import { GisEmptyState, GisErrorState, GisLoadingSkeleton } from './GisStates'
import { AnalyticsPanel } from './panel/AnalyticsPanel'
import { DashboardButton, type DashboardBoard } from './panel/dashboard/DashboardButton'
import { DashboardOverlay } from './panel/dashboard/DashboardOverlay'
import { PanelTabs } from './panel/PanelTabs'
import { PeriodDropdown } from './panel/PeriodDropdown'
import { ViewModeToggle } from './panel/ViewModeToggle'
import { SplitHandle } from './SplitHandle'
import { useGisIssues } from '../hooks/useGisIssues'
import { useGisMandals } from '../hooks/useGisMandals'
import { useGisSelection } from '../hooks/useGisSelection'
import { useGisSites } from '../hooks/useGisSites'
import { useGisTrends } from '../hooks/useGisTrends'
import {
  useSplitPane,
  PANEL_MIN_PX,
  PANEL_DEFAULT_FRACTION,
  PANEL_MAX_VW,
} from '../hooks/useSplitPane'
import { attention } from '../lib/attention'
import { countByMandal, filterByFacets } from '../lib/issue-board'
import { issuesBySite, siteIssueCounts } from '../lib/issues'
import { useGisIssueView } from '../stores/gisIssueViewStore'

import type { GisLens, GisOverview, GisPeriod, GisScope, GisTab } from '../api/types'
import type { GisSelection } from '../hooks/useGisSelection'
import type { MapMetric } from '../layers/mandalChoroplethLayer'
import type { HodOpenRef } from '../lib/detail-stack'

const EMPTY_TRENDS: GisOverview['trends'] = { coverage: [], issues: [], officers: [] }

const routeApi = getRouteApi('/gis')

// Cold-load / failure / no-data full-pane fallbacks (design §: keepPreviousData means these only show on cold loads).
function gisFallbackView(args: {
  isPending: boolean
  isError: boolean
  overview: GisOverview | undefined
  onRetry: () => void
}): ReactElement | null {
  if (args.isPending) {
    return <GisLoadingSkeleton />
  }
  if (args.isError) {
    return <GisErrorState onRetry={args.onRetry} />
  }
  if (!args.overview || args.overview.mandals.length === 0) {
    return <GisEmptyState />
  }
  return null
}

// Resolve the active tab/period from the URL (with defaults) and the map lens. The Dashboard
// (`overview` key) is the default landing tab — observation first, one click into the boards.
// Overview keeps the last active lens; any other tab IS the lens (design §5).
function resolveTabState(
  tabParam: GisTab | undefined,
  periodParam: GisPeriod | undefined,
  lastLens: GisLens,
): { tab: GisTab; period: GisPeriod; lens: GisLens } {
  const tab: GisTab = tabParam ?? 'overview'
  const period: GisPeriod = periodParam ?? '30d'
  const lens: GisLens = tab === 'overview' ? lastLens : tab
  return { tab, period, lens }
}

// The map choropleth metric per tab: attention on Overview (the ranking spine), the filter-reactive
// issue count on Issues, else the lens % (Coverage/Officers).
function mapMetricForTab(tab: GisTab): MapMetric {
  return tab === 'coverage'
    ? 'coverage'
    : tab === 'officers'
      ? 'officers'
      : tab === 'issues'
        ? 'issues'
        : 'attention'
}

// Flex sizing for the list panel (V5: the panel is the LEFT pane, the map the right). Full mode =
// no map, so the panel always fills the width. In Split it's a resizable column: ~63% normally
// (map ~37%), or ~⅔ when the SITE drill fills it (the site view needs room; issue/HOD details pop
// over the map instead). `widthPx` (the drag) overrides the default fraction.
function asideStyle(full: boolean, widthPx: number | null, siteOpen: boolean): CSSProperties {
  if (full) {
    return { flex: '1 1 auto' }
  }
  const defaultFraction = siteOpen ? 2 / 3 : PANEL_DEFAULT_FRACTION
  const basis = widthPx !== null ? `${widthPx}px` : `${defaultFraction * 100}%`
  const maxVw = siteOpen ? 0.82 : PANEL_MAX_VW
  return { flex: `0 0 ${basis}`, minWidth: siteOpen ? 680 : PANEL_MIN_PX, maxWidth: `${maxVw * 100}vw` }
}

// Stable empty reference so the map's style/fly-to effects don't re-run every render when no mandal
// is selected (a fresh `[]` each render would re-fit the camera constantly).
const EMPTY_MANDALS: string[] = []

/** Tiny predicates so the page body stays under the complexity budget. */
const isOfficerSelection = (sel: GisSelection): boolean => sel?.kind === 'officer'
const isSiteFrame = (frame: { kind: string } | null): boolean => frame?.kind === 'site'

export function GisViewPage() {
  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const entityId = search.entityId
  // Mandal is a URL-backed multi-select filter, stored as a comma-joined string (`?mandal=A,B`).
  const activeMandals = useMemo(
    () => (search.mandal ? search.mandal.split(',').filter(Boolean) : EMPTY_MANDALS),
    [search.mandal],
  )
  // Overview keeps the map on the last active lens (design §5).
  const [lastLens, setLastLens] = useState<GisLens>('coverage')
  // The per-board dashboard WINDOW (tab-bar Dashboards dropdown) — null when closed.
  const [dashboardBoard, setDashboardBoard] = useState<DashboardBoard | null>(null)
  const { tab, period, lens } = resolveTabState(search.tab, search.period, lastLens)
  const issuesFocus = tab === 'issues'
  const coverageFocus = tab === 'coverage'
  // Overview shades by the Attention Score (the ranking spine); Issues by the filter-reactive issue
  // count; Coverage/Officers by %.
  const mapMetric = mapMetricForTab(tab)
  const statusView = useGisIssueView((s) => s.statusView)
  const issueSeverity = useGisIssueView((s) => s.severity)
  const issueCategory = useGisIssueView((s) => s.category)
  const issueType = useGisIssueView((s) => s.issueType)
  const issueEntity = useGisIssueView((s) => s.entity)
  const issueDepartment = useGisIssueView((s) => s.department)
  const issueHod = useGisIssueView((s) => s.hod)
  const issueQuery = useGisIssueView((s) => s.query)
  const issueAgeBands = useGisIssueView((s) => s.ageBands)
  const issueSiteNames = useGisIssueView((s) => s.siteNames)

  const scope: GisScope = { period, entityId, dateFrom: search.dateFrom, dateTo: search.dateTo }
  const { data, isPending, isError, refetch } = useGisMandals(scope)
  // Markers + trends load independently of the choropleth; they fold into the view-model as they arrive.
  const { data: sites } = useGisSites(scope)
  const { data: trends } = useGisTrends(scope)
  // Drives the map's "Issue hotspots" rings + marker visibility. Off the Issues tab the rings show all
  // active issues. NOTE: mandal is deliberately excluded from this feed (see `issueFacets`) so the map
  // stays a full-district picture; the panel does its own mandal scoping.
  const { data: issuesPage } = useGisIssues(scope)
  const siteMandal = useMemo(() => new Map((sites ?? []).map((s) => [s.id, s.mandal])), [sites])
  // The map-layer facet set — the cross-filtering spine for the map's marker rings, marker visibility,
  // and choropleth counts. Mandal is intentionally EXCLUDED so selecting a mandal never removes another
  // mandal's markers/rings/counts (it only highlights the polygon + scopes the side panel). Every other
  // facet — including the V5 HOD facet and the toolbar search — cross-filters the map in lockstep.
  const issueFacets = useMemo(
    () => ({
      statusView,
      severity: issueSeverity,
      category: issueCategory,
      issueType,
      entity: issueEntity,
      department: issueDepartment,
      hod: issueHod,
      query: issueQuery,
      mandal: EMPTY_MANDALS,
      age: issueAgeBands,
      site: issueSiteNames,
    }),
    [statusView, issueSeverity, issueCategory, issueType, issueEntity, issueDepartment, issueHod, issueQuery, issueAgeBands, issueSiteNames],
  )
  // Faceted feed computed ONCE, reused for the rings + the per-mandal counts (avoids double-filtering).
  const filteredFeed = useMemo(
    () => filterByFacets(issuesPage?.issues ?? [], issueFacets, siteMandal),
    [issuesPage, issueFacets, siteMandal],
  )
  const issueHotspots = useMemo(
    () => (issuesFocus ? siteIssueCounts(filteredFeed) : issuesBySite(issuesPage?.issues ?? [])),
    [issuesFocus, filteredFeed, issuesPage],
  )
  // Filter-reactive issue count per mandal — the Issues-tab choropleth fill + labels (the count
  // analogue of `attentionByName`). Consumed only when the map metric is 'issues'.
  const mandalIssueCountByName = useMemo(
    () => countByMandal(filteredFeed, siteMandal),
    [filteredFeed, siteMandal],
  )
  // Attention score per mandal (the Overview choropleth fill) — exact, from the server-computed
  // open-issue fields on each mandal row (no client feed join).
  const attentionByName = useMemo(
    () => new Map((data?.mandals ?? []).map((m) => [m.name, attention(m).score])),
    [data?.mandals],
  )
  const overview: GisOverview | undefined = useMemo(
    () =>
      data
        ? {
            ...data,
            sites: sites ?? [],
            trends: trends?.series ?? EMPTY_TRENDS,
            trendBuckets: trends?.buckets ?? [],
            trendBucketSize: trends?.bucketSize ?? '',
          }
        : undefined,
    [data, sites, trends],
  )
  // Picking a preset (7d/30d/90d/FY) clears any custom range; the popover sets period='custom' + dates.
  const setPeriod = (next: GisPeriod) =>
    void navigate({
      search: (s) => ({ ...s, period: next, dateFrom: undefined, dateTo: undefined }),
    })
  const setCustomRange = (from: string, to: string) =>
    void navigate({ search: (s) => ({ ...s, period: 'custom', dateFrom: from, dateTo: to }) })
  const { selection, setSelection, clear, currentFrame, detailBackLabel, pushDetail, popDetail, clearDetail, mapSelection } =
    useGisSelection()

  const split = useSplitPane()
  const { setOpen: splitSetOpen } = split

  // Esc closes the dashboard window first, then an open drill (overlay), then the scope selection.
  // Never while typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return
      }
      if (e.target instanceof HTMLElement && e.target.closest('input,select,textarea')) {
        return
      }
      if (dashboardBoard !== null) {
        setDashboardBoard(null)
        return
      }
      if (currentFrame) {
        clearDetail()
        return
      }
      if (selection) {
        clear()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [dashboardBoard, currentFrame, selection, clearDetail, clear])

  // Panel is always present (Split View / Full Analytics — never closed), so splitSetOpen(true) stays
  // a harmless no-op kept for safety if a close path is ever re-introduced.
  const select = useCallback(
    (next: NonNullable<GisSelection>) => {
      setSelection(next)
      splitSetOpen(true) // idempotent; reopens the panel when a selection comes from search/map while closed
    },
    [setSelection, splitSetOpen],
  )

  // The mandal is a URL-backed multi-select page filter (comma-joined) — clicking a mandal toggles it
  // in/out of the selection; the last one removed clears the param.
  const onSelectMandal = useCallback(
    (name: string) =>
      void navigate({
        search: (s) => {
          const current = s.mandal ? s.mandal.split(',').filter(Boolean) : []
          const next = current.includes(name)
            ? current.filter((m) => m !== name)
            : [...current, name]
          return { ...s, mandal: next.length ? next.join(',') : undefined }
        },
      }),
    [navigate],
  )
  const onClearMandal = useCallback(
    () => void navigate({ search: (s) => ({ ...s, mandal: undefined }) }),
    [navigate],
  )
  // Dashboard entity scope — URL-backed (`?entityId=`); the server re-scopes every query with it.
  const onEntityChange = useCallback(
    (id: string | undefined) => void navigate({ search: (s) => ({ ...s, entityId: id }) }),
    [navigate],
  )
  // Site + issue push detail frames (the drill stack). A site click from the map (Split only) and
  // from a list row share this handler — the site view fills the list panel in both modes.
  const onSelectSite = useCallback(
    (id: string) => {
      pushDetail({ kind: 'site', siteId: id, rootLabel: tab === 'coverage' ? 'Coverage' : 'Issues' })
      splitSetOpen(true)
    },
    [pushDetail, splitSetOpen, tab],
  )
  const onSelectOfficer = useCallback(
    (id: string, name: string) => {
      // Officer detail renders in the panel body (selection), so close any open drill overlay first —
      // otherwise opening the officer from an issue's Responsibility card leaves the issue overlay on top.
      clearDetail()
      select({ kind: 'officer', id, name })
    },
    [clearDetail, select],
  )
  const onSelectIssue = useCallback(
    (id: string, siteId: string | null = null) => {
      pushDetail({ kind: 'issue', id, siteId, rootLabel: tab === 'coverage' ? 'Coverage' : 'Issues' })
      splitSetOpen(true)
    },
    [pushDetail, splitSetOpen, tab],
  )
  const onOpenHod = useCallback(
    (hod: HodOpenRef) =>
      pushDetail({
        kind: 'hod',
        department: hod.department,
        hodName: hod.name ?? null,
        hodPhone: hod.phone ?? null,
        hodEmail: hod.email ?? null,
        rootLabel: 'Issues',
      }),
    [pushDetail],
  )

  const setTab = (next: GisTab) => {
    if (next !== 'overview') {
      setLastLens(next)
    }
    // Tabs and scope are orthogonal: a mandal selection persists across tab changes (drill-down).
    // Close any open detail drill and drop an officer selection; keep a mandal scope.
    clearDetail()
    setSelection((cur) => (cur?.kind === 'officer' ? null : cur))
    void navigate({ search: (s) => ({ ...s, tab: next }) })
  }

  // Full-pane states: skeleton on cold load, error on failure, empty when no mandals.
  // With placeholderData: keepPreviousData, isPending stays false during entity/period switches
  // so the split layout persists during refetches — only cold loads show the skeleton.
  const fallback = gisFallbackView({ isPending, isError, overview, onRetry: () => void refetch() })
  if (fallback || !overview) {
    return fallback
  }

  // V5 layout: full-width tab bar (Issues|Coverage · period · Split/Full) + full-width Issues
  // toolbar, then the content row — LIST panel left, map right. The map is tied to the Split/Full
  // toggle: shown in Split, hidden in Full. In Split, an open issue/HOD detail pops up OVER the map
  // (site details fill the list panel instead); in Full the detail is the panel's right drawer.
  const mapHidden = split.full
  const officerOpen = isOfficerSelection(selection)
  const siteOpen = isSiteFrame(currentFrame)

  return (
    <div className="flex h-full min-h-0 flex-col bg-gis-bg">
      <PanelTabs
        tab={tab}
        onChange={setTab}
        right={
          <>
            <DashboardButton tab={tab} onOpen={setDashboardBoard} />
            <PeriodDropdown
              period={period}
              dateFrom={search.dateFrom}
              dateTo={search.dateTo}
              onPeriodChange={setPeriod}
              onCustomRange={setCustomRange}
            />
            <ViewModeToggle full={split.full} onToggle={split.toggleFull} />
          </>
        }
      />

      <GisHeaderToolbar
        tab={tab}
        officerOpen={officerOpen}
        scope={scope}
        issuesPage={issuesPage}
        siteMandal={siteMandal}
        sites={overview.sites}
        mandals={overview.mandals}
        activeMandals={activeMandals}
        onSelectMandal={onSelectMandal}
        onClearMandal={onClearMandal}
        onEnterGallery={clearDetail}
        entityId={entityId}
        onEntityChange={onEntityChange}
      />

      <div className="relative flex min-h-0 flex-1">
        {/* list / analytics panel — the LEFT pane */}
        {split.open && (
          <aside
            className="relative flex min-h-0 flex-col border-r border-gis-line bg-gis-panel"
            style={asideStyle(split.full, split.widthPx, siteOpen)}
          >
            <AnalyticsPanel
              overview={overview}
              tab={tab}
              period={period}
              dateFrom={search.dateFrom}
              dateTo={search.dateTo}
              entityId={entityId}
              selection={selection}
              activeMandals={activeMandals}
              full={split.full}
              onTabChange={setTab}
              onSelectMandal={onSelectMandal}
              onSelectSite={onSelectSite}
              onSelectIssue={onSelectIssue}
              onSelectOfficer={onSelectOfficer}
              onClearSelection={clear}
              detailFrame={currentFrame}
              detailBackLabel={detailBackLabel}
              onDetailBack={popDetail}
              onDetailClose={clearDetail}
              onOpenHod={onOpenHod}
            />
          </aside>
        )}

        {split.open && !mapHidden && <SplitHandle onPointerDown={split.onHandlePointerDown} />}

        {/* map pane — the RIGHT pane (Split only); issue/HOD details pop up over it */}
        {!mapHidden && (
          <GisMapRegion
            overview={overview}
            lens={lens}
            mapMetric={mapMetric}
            attentionByName={attentionByName}
            issueCountByName={mandalIssueCountByName}
            issuesPage={issuesPage}
            issuesBySite={issueHotspots}
            issuesFocus={issuesFocus}
            coverageFocus={coverageFocus}
            selection={mapSelection}
            activeMandals={activeMandals}
            scope={scope}
            frame={currentFrame}
            backLabel={detailBackLabel}
            onLensChange={setTab}
            onSelectMandal={onSelectMandal}
            onSelectSite={onSelectSite}
            onClearSelection={clear}
            onCloseMap={split.toggleFull}
            onDetailBack={popDetail}
            onDetailClose={clearDetail}
            onOpenIssue={onSelectIssue}
            onOpenHod={onOpenHod}
            onSelectOfficer={onSelectOfficer}
          />
        )}
      </div>

      {/* Per-board dashboard window (Dashboards dropdown in the tab bar) — on top of everything. */}
      {dashboardBoard !== null && (
        <DashboardOverlay
          board={dashboardBoard}
          overview={overview}
          scope={scope}
          activeMandals={activeMandals}
          onClose={() => setDashboardBoard(null)}
          onOpenLens={setTab}
          onSelectMandal={onSelectMandal}
          onSelectIssue={onSelectIssue}
        />
      )}
    </div>
  )
}
